import React, { useRef, useState } from 'react';
import DishList from './DishList';
import LoadingComponent from './LoadingComponent';
import api from '../api';

const OPTION_GROUPS = [
  {
    key: 'mealType',
    label: 'Meal Type',
    icon: '🍽️',
    options: [
      { value: 'Breakfast', icon: '🌅' },
      { value: 'Lunch',     icon: '☀️' },
      { value: 'Snacks',    icon: '🥪' },
      { value: 'Dinner',    icon: '🌙' },
    ],
  },
  {
    key: 'cuisine',
    label: 'Cuisine',
    icon: '🌶️',
    options: [
      { value: 'Indian',   icon: '🇮🇳' },
      { value: 'Italian',  icon: '🇮🇹' },
      { value: 'Chinese',  icon: '🇨🇳' },
      { value: 'Mexican',  icon: '🇲🇽' },
    ],
  },
  {
    key: 'dietRestrictions',
    label: 'Dietary Restriction',
    icon: '🥗',
    options: [
      { value: 'Vegan',          icon: '🌱' },
      { value: 'Vegetarian',     icon: '🥦' },
      { value: 'Non-Vegetarian', icon: '🍗' },
      { value: 'Gluten-Free',    icon: '🌾' },
    ],
  },
  {
    key: 'diet',
    label: 'Specific Diet',
    icon: '💪',
    options: [
      { value: 'keto',          icon: '🥩' },
      { value: 'Paleo',         icon: '🦴' },
      { value: 'Low Carb',      icon: '📉' },
      { value: 'High Protein',  icon: '💪' },
      { value: 'Weight Loss',   icon: '⚖️' },
      { value: 'Weight Gain',   icon: '📈' },
    ],
  },
  {
    key: 'cookingTime',
    label: 'Cooking Time',
    icon: '⏱️',
    options: [
      { value: '<30 Minutes',    icon: '⚡' },
      { value: '30-60 Minutes',  icon: '⏰' },
      { value: '>60 Minutes',    icon: '🍲' },
    ],
  },
];

function PersonalizationForm({ ingredients, onBack }) {
  const [dishList, setDishList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isSubmitting = useRef(false);
  const [options, setOptions] = useState({
    cuisine: '', mealType: '', dietRestrictions: '', diet: '', cookingTime: '',
  });

  const select = (key, value) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (isSubmitting.current) return;

    isSubmitting.current = true;
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/recipe/personalize/dishes', { ingredients, options });
      const data = Array.isArray(response.data) ? response.data : [];
      const validationError = data.find(
        (item) => typeof item === 'string' && (
          item.startsWith('Error:') ||
          item.includes('Please recheck your personalized options.') ||
          item.includes('non-edible') ||
          item.includes('toxic')
        )
      );

      if (validationError) {
        const message = validationError.replace(/^Error:\s*/, '').trim();
        setError(message);
        return;
      }

      setDishList(data);
    } catch (err) {
      const msg = err.response?.data?.message || 'There was an error fetching the dishes. Please try again.';
      setError(msg);
    } finally {
      isSubmitting.current = false;
      setLoading(false);
    }
  };

  if (dishList) return <DishList dishes={dishList} onBack={() => setDishList(null)} />;
  if (loading)  return <LoadingComponent />;

  return (
    <div className="content-panel">
      <button className="back-btn" onClick={onBack}>&#8592; Back</button>

      <div className="section-header">
        <h2>Personalise Your Recipe</h2>
        <p>Select your preferences to find the perfect dish.</p>
      </div>

      {error && (
        <div style={{
          padding: '14px 18px',
          background: 'var(--color-danger-light)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-md)',
          color: 'rgb(153, 27, 27)',
          fontSize: '0.9rem',
          marginBottom: '20px',
        }}>
          ⚠️ {error}
        </div>
      )}

      {OPTION_GROUPS.map((group) => (
        <div key={group.key} className="card-custom" style={{ padding: '24px', marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.82rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--color-text-muted)',
            marginBottom: '14px',
          }}>
            <span>{group.icon}</span> {group.label}
          </label>

          <div className="option-cards-grid">
            {group.options.map((opt) => (
              <label
                key={opt.value}
                className={`option-card ${options[group.key] === opt.value ? 'selected' : ''}`}
                onClick={(e) => { e.preventDefault(); select(group.key, opt.value); }}
              >
                <input
                  type="radio"
                  name={group.key}
                  value={opt.value}
                  checked={options[group.key] === opt.value}
                  onChange={() => {}}
                />
                <span className="option-icon">{opt.icon}</span>
                <span className="option-label">{opt.value}</span>
              </label>
            ))}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '12px', paddingTop: '8px', paddingBottom: '40px' }}>
        <button className="btn-green-custom" onClick={handleSubmit}>
          🍳 Generate Recipes
        </button>
        <button className="btn-secondary-custom" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}

export default PersonalizationForm;
